import React from 'react';
import { push } from 'react-router-redux';
import gettext from '@eb/gettext';
import { SubmissionError, change } from 'redux-form';
import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';
import isEmpty from 'lodash/isEmpty';

import { joinPath } from '@eb/path-utils';
import { setWindowLocation } from '@eb/http';
import {
    trackEventFromState,
    trackEventFromStateOnPageUnload,
} from '@eb/site-analytics';
import { SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE } from '@eb/social-login-facebook';
import { parseFormPostData } from '../api/utils';
import { getSetPasswordEvent, notifyParent } from '../utils';
import {
    getLogInFormData,
    getSignUpFormData,
    handleResetPasswordAccountStatusResponse,
    handleLookupUserResponse,
    handleLookupUserResponseForCheckout,
    handleLookupUserResponseForLinking,
    shouldOnboardingRedirect,
} from './utils';
import {
    lookupUser as lookupUserApi,
    logInUser as logInUserApi,
    signUpUser as signUpUserApi,
    sendResetPasswordEmail as sendResetPasswordEmailApi,
    sendSoftAccountActivationEmail as sendSoftAccountActivationEmailApi,
    setPassword as setPasswordApi,
    loginWithToken as loginWithTokenApi,
    postAddExternalIdentity,
    setLinkedAccountApi,
    sendEmailLinkAccountApi,
} from '../api';
import {
    RESET_PASSWORD_STATUS_PATH,
    LOG_IN_FORM,
    LINK_ACCOUNTS_LOG_IN_FORM,
    SOFT_ACCOUNT_STATUS_PATH,
    BASE_URL,
    CHECKOUT_PATH,
    GA_AUTH_CATEGORY,
    GA_AUTH_CHECKOUT_CATEGORY,
    GA_CONFIRM_EMAIL_SHOWED,
    GA_RE_ENTER_EMAIL_LABEL,
    GA_EDIT_EMAIL_EVENT,
    GA_EDIT_EMAIL_MISMATCH,
    GA_SIGNUP_EVENT,
    GA_LOGIN_EVENT,
    GA_LINK_ACCOUNTS_LOGIN_EVENT,
    GA_LOGIN_INSTEAD_CLICKED_EVENT,
    GA_CHECK_EMAIL_PROMPT_EVENT,
    GA_SOFT_ACCT_LABEL,
    CAPTCHA_FIELD,
    ARGUMENTS_ERROR,
    EXTERNAL_USER_EMAIL_ALREADY_USED_ERROR,
    EXTERNAL_USER_UNKNOWN_ERROR,
    EXTERNAL_USER_WITHOUT_EMAIL_ERROR,
    EMAIL_PERMISSION_DENIED_ERROR,
    TOKEN_NOT_AUTHORIZED_ERROR,
    INVALID_SOCIAL_LOGIN_FIELD,
    GA_FB_LOGIN_NO_EMAIL_ADDRESS,
    GA_FB_LOGIN_EMAIL_NOT_SHARED,
    GA_FB_LOGIN_EMAIL_ALREADY_EXISTS,
    GA_FB_LOGIN_EMAIL_ALREADY_EXISTS_ON_SOFT_ACCOUNT,
    GA_FB_LOGIN_NOT_LOGGED_IN_TO_FB,
    GA_FB_LOGIN_EXTERNAL_USER_UNKNOWN,
    GA_FB_LOGIN_ACCOUNT_LINKING_FAILED,
    SOCIAL_LOG_IN_PATH,
    SOCIAL_LOOK_UP_PATH,
    SOCIAL_SIGN_UP_PATH,
    LINK_ACCOUNTS_LOG_IN_PATH,
    EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR,
    SET_LINKED_SOFT_ACCOUNTS_PATH,
    SUSPENDED_ACCOUNT,
    EXTERNAL_UNKNOWN_ERROR,
    SET_LINK_ACCOUNT_OK,
    SET_LINK_ACCOUNT_ERROR,
    SET_LINK_ACCOUNT_TOKEN_EXPIRED,
    SET_LINK_ACCOUNT_OTHER_ERROR,
    LINK_SOCIAL_ACCOUNT_PATH,
} from '../constants';

import { EVENTBRITE_PROVIDER } from '../constants/newIndex';

import {
    EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR_MESSAGE,
    LINK_ACCOUNTS_OPTION_FOR_EXTERNAL_USER_EMAIL,
    LINK_ACCOUNTS_OPTION_FOR_EXTERNAL_USER_EMAIL_ML,
    EMAIL_PERMISSION_DENIED_ERROR_MESSAGE,
    TOKEN_NOT_AUTHORIZED_ERROR_MESSAGE,
    UNKNOWN_ERROR_MESSAGE,
    UNKNOWN_EXTERNAL_PROVIDER_ERROR_MESSAGE,
    LOCKED_ACCOUNT_ERROR_MESSAGE,
    FACEBOOK_ACCOUNT_NOT_LOGGED_IN_ERROR,
} from '../constants/messages';

import {
    trackSIWA_AccountCreatedWithHiddenEmail,
    trackSIWA_errorOnNewUser,
    trackLoginClicked,
    trackPasswordForgotten,
    trackPasswordForgottenMailFailed,
    trackPasswordForgottenMailSent,
    trackAccountLinked,
    trackMagicLinkSent,
    trackMagicLinkClicked,
    trackMagicLinkFailed,
    trackMagicLinkConfirmationClosed,
    trackAccountLinkFailed,
    trackAccountLinkConfirmationClosed,
    trackUseAnotherAccountClicked,
    trackSignUpClicked,
    trackPasswordIncorrect,
    trackPasswordViewed,
    trackPasswordReseted,
    trackPasswordResetedFailed,
    trackLoginSuccessful,
} from './gaTracking';

import { handleAppleSocialLogin } from './appleLogin';

import { AlertChunky } from '@eb/eds-iconography';

export const INVALID_PASSWORD_RESET_TOKEN_ACTION = 'invalidToken';
export const invalidPasswordResetToken = () => ({
    type: INVALID_PASSWORD_RESET_TOKEN_ACTION,
    payload: true,
});

export const SET_SOCIAL_DATA_ACTION = 'setSocialDataAction';
export const setSocialData = ({
    accessToken,
    sourceProvider,
    email,
    firstName,
    lastName,
    userPicture,
}) => ({
    type: SET_SOCIAL_DATA_ACTION,
    payload: {
        accessToken,
        sourceProvider,
        email,
        firstName,
        lastName,
        userPicture,
    },
});

export const SET_EMAIL_ACTION = 'setEmail';
export const setEmail = (email) => ({
    type: SET_EMAIL_ACTION,
    payload: email,
});

export const CLEAR_EMAIL_ACTION = 'clearEmail';
export const clearEmail = () => ({
    type: CLEAR_EMAIL_ACTION,
});

export const FORCE_STLD = 'forceStld';
export const forceStld = () => ({
    type: FORCE_STLD,
});

export const SHOW_CAPTCHA = 'showCaptcha';
export const showCaptcha = () => ({
    type: SHOW_CAPTCHA,
});

export const HIDE_CAPTCHA = 'hideCaptcha';
export const hideCaptcha = () => ({
    type: HIDE_CAPTCHA,
});

export const SHOW_AUTH_NOTIFICATION = 'showAuthNotification';
export const showAuthNotification = ({ message, type, iconType }) => ({
    type: SHOW_AUTH_NOTIFICATION,
    payload: {
        message,
        type,
        iconType,
    },
});

export const CLEAR_AUTH_NOTIFICATION = 'clearAuthNotification';
export const clearAuthNotification = () => ({
    type: CLEAR_AUTH_NOTIFICATION,
});

export const SET_ACCOUNT_EXISTENT = 'setAccountExistent';
export const setAccountExistent = (email) => ({
    type: SET_ACCOUNT_EXISTENT,
    payload: email,
});

export const SHOW_LINKED_ACCOUNTS_DIALOG = 'showLinkedAccountsDialog';
export const showLinkedAccountsDialog = (redirectUrl) => ({
    type: SHOW_LINKED_ACCOUNTS_DIALOG,
    payload: {
        redirectUrl,
    },
});

export const RE_REQUEST_PERMISSIONS = 'setReRequestPermissionStatus';
export const setReRequestPermissionStatus = () => ({
    type: RE_REQUEST_PERMISSIONS,
    payload: true,
});

export const CLEAN_SOCIAL_DATA_ACTION = 'cleanSocialDataAction';
export const cleanSocialDataAction = () => ({
    type: CLEAN_SOCIAL_DATA_ACTION,
});

export const CLEAN_SOCIAL_AUTHENTICATION_DATA_ACTION =
    'cleanSocialAuthenticationDataAction';
export const cleanSocialAuthenticationDataAction = () => ({
    type: CLEAN_SOCIAL_AUTHENTICATION_DATA_ACTION,
});
export const ADD_SOCIAL_AUTHENTICATION_DATA_ACTION =
    'addSocialAuthenticationDataAction';
export const addSocialAuthenticationDataAction = (payload) => ({
    type: ADD_SOCIAL_AUTHENTICATION_DATA_ACTION,
    payload,
});

export const SET_SOCIAL_LINKED_ACCOUNT = 'SET_SOCIAL_LINKED_ACCOUNT';
export const setSocialLinkedAccount = (socialLinkedAccountResult) => ({
    type: SET_SOCIAL_LINKED_ACCOUNT,
    payload: socialLinkedAccountResult,
});

export const SET_SEND_EMAIL_LINK_ACCOUNT = 'SET_SEND_EMAIL_LINK_ACCOUNT';
export const setSendEmailLinkAccount = (sent) => ({
    type: SET_SEND_EMAIL_LINK_ACCOUNT,
    payload: sent,
});

export const SET_IS_LOADING = 'SET_IS_LOADING';
export const setIsLoading = (isLoading) => ({
    type: SET_IS_LOADING,
    payload: isLoading,
});

export const SET_SHOW_MODAL = 'SET_SHOW_MODAL';
export const setShowModal = (show) => ({
    type: SET_SHOW_MODAL,
    payload: show,
});

export const SIWA_TIMER_TIMEOUT = 'SIWA_TIMER_TIMEOUT';
export const timeoutSIWATimer = ({ message, type, iconType }) => ({
    type: SIWA_TIMER_TIMEOUT,
    payload: {
        message,
        type,
        iconType,
    },
});
export const SIWA_TIMER_TICK = 'SIWA_TIMER_TICK';
export const tickSIWATimer = () => ({
    type: SIWA_TIMER_TICK,
});

export const UPDATE_CURRENT_FORM_TYPE_ACTION =
    'UPDATE_CURRENT_FORM_TYPE_ACTION';
export const updateCurrentFormType = (data) => ({
    type: UPDATE_CURRENT_FORM_TYPE_ACTION,
    payload: {
        formType: data,
    },
});

export const updateFormType = (path = '') => (dispatch) => {
    //Any time we change form want to reset our captcha display logic.
    //Decision service will decide if it will be presented on submission.
    dispatch(hideCaptcha());
    dispatch(push(joinPath(BASE_URL, path)));
};

export const updateCheckoutFormType = (path = '') => (dispatch) => {
    //Any time we change form want to reset our captcha display logic.
    //Decision service will decide if it will be presented on submission.
    dispatch(hideCaptcha());
    dispatch(updateCurrentFormType(path));
    dispatch(push(joinPath(BASE_URL, CHECKOUT_PATH, path)));
};

export const cleanSocialAuthenticationData = () => (dispatch) => {
    //Any time we want to reset the social authentication data we dispatch this action.
    dispatch(cleanSocialAuthenticationDataAction());
    dispatch(cleanSocialDataAction());
};

export const getEmailFromState = (getState) => {
    const state = getState();
    const {
        authentication: {
            email,
            socialAuthenticationData: { apple: { email: _socialEmail } = {} },
        } = {},
    } = state;

    return email || _socialEmail || '';
};

export const handleOnError = (dispatch, state, data, actionCategory) => (
    error,
) => {
    const gaActionCategory = actionCategory ? actionCategory : GA_AUTH_CATEGORY;

    if (error.name === 'CAPTCHA_REQUIRED') {
        //we need to dispatch an action to handle displaying the captcha, so this specific error
        //lives slightly outside of the standard error protocol. Have it here because proxying the
        //specific error for requiring the captcha to this point is cleaner than inserting dispatch
        //directly into the response handling itself
        dispatch(showCaptcha());

        //once we are here in the response handling and have access to dispatch, we need to continue
        //passing along the error to redux form such that its lifecycle methods correctly represent the
        //failed submission state. We don't want to actually display a form error, as at this stage
        //showing the captcha
        throw new SubmissionError({
            [CAPTCHA_FIELD]: gettext('Complete to proceed'),
        });
    }

    // Scenario: facebookEmailDenied
    if (error.name === EXTERNAL_USER_EMAIL_ALREADY_USED_ERROR) {
        // dispatch action to show notification here
        // so the error can be seen on the screen
        const {
            authentication: {
                socialData,
                featureFlags: { launchSendLinkAccountEmail },
            },
        } = state;

        // Merge current social data with possible changes made by user
        const updatedSocialData = { ...socialData, ...data };

        dispatch(
            showAuthNotification({
                message: launchSendLinkAccountEmail
                    ? LINK_ACCOUNTS_OPTION_FOR_EXTERNAL_USER_EMAIL_ML
                    : LINK_ACCOUNTS_OPTION_FOR_EXTERNAL_USER_EMAIL,
                iconType: launchSendLinkAccountEmail ? null : <AlertChunky />,
            }),
        );

        trackEventFromState(state, {
            action: GA_FB_LOGIN_EMAIL_ALREADY_EXISTS,
            category: gaActionCategory,
            label: launchSendLinkAccountEmail
                ? 'FF_MagicLinkFB_Enabled'
                : 'FF_MagicLinkFB_Disabled',
        });

        /*
        The subsequent routes (login/signup/etc) expect email to be in state, and the
        account status to be checked, so need to update store before dealing with the response
        logic. When setting account status as checked, the email reducer is
        also listening and will update.
        */
        if (updatedSocialData && updatedSocialData.email) {
            dispatch(setAccountExistent(updatedSocialData.email));
        }
        if (updatedSocialData) {
            dispatch(setSocialData(updatedSocialData));
        }

        dispatch(updateFormType(LINK_ACCOUNTS_LOG_IN_PATH));
        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    // Scenario: facebookSoftAccount
    if (error.name === EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR) {
        dispatch(
            showAuthNotification({
                message: EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );

        trackEventFromState(state, {
            action: GA_FB_LOGIN_EMAIL_ALREADY_EXISTS_ON_SOFT_ACCOUNT,
            category: gaActionCategory,
        });

        if (data && data.email) {
            dispatch(setAccountExistent(data.email));
        }
        dispatch(updateFormType(SET_LINKED_SOFT_ACCOUNTS_PATH));

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    // Scenario: facebookEmailNotProvided
    if (error.name === EXTERNAL_USER_WITHOUT_EMAIL_ERROR) {
        // dispatch action to redirect to social sign up
        // using data brought from external source
        if (data && (data.firstName || data.lastName)) {
            dispatch(setSocialData(data));
        }

        dispatch(updateFormType(SOCIAL_SIGN_UP_PATH));

        trackEventFromState(state, {
            action: GA_FB_LOGIN_NO_EMAIL_ADDRESS,
            category: gaActionCategory,
        });

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    // Scenario: facebookEmailDenied
    if (error.name === EMAIL_PERMISSION_DENIED_ERROR) {
        // dispatch action that makese the FacebookLoginComponent re request permissions.
        dispatch(setReRequestPermissionStatus());

        // dispatch action to show notification here
        // so the error can be seen on the screen
        dispatch(
            showAuthNotification({
                message: EMAIL_PERMISSION_DENIED_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );

        trackEventFromState(state, {
            action: GA_FB_LOGIN_EMAIL_NOT_SHARED,
            category: gaActionCategory,
        });

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    // Scenario: facebookUnknownUser
    if (error.name === EXTERNAL_USER_UNKNOWN_ERROR) {
        // dispatch action to redirect to social sign up
        // using data brought from external source
        let formType = SOCIAL_SIGN_UP_PATH;
        let message = null;

        lookupUserApi(data.email).then(({ exists, can_login: canLogin }) => {
            // If user exists, skip confirmation view
            if (exists) {
                // If user has full account, redirect to link accounts view
                if (canLogin) {
                    message = LINK_ACCOUNTS_OPTION_FOR_EXTERNAL_USER_EMAIL;
                    formType = LINK_ACCOUNTS_LOG_IN_PATH;
                    // If user has soft account, redirect to create password view
                } else {
                    message = EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR_MESSAGE;
                    formType = SET_LINKED_SOFT_ACCOUNTS_PATH;
                }
                dispatch(setAccountExistent(data.email));
                dispatch(
                    showAuthNotification({
                        message,
                        iconType: <AlertChunky />,
                    }),
                );
            }

            dispatch(setSocialData(data));
            dispatch(updateFormType(formType));
        });

        trackEventFromState(state, {
            action: GA_FB_LOGIN_EXTERNAL_USER_UNKNOWN,
            category: gaActionCategory,
        });

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    // Scenario: facebookTokenNotAuthorized
    if (error.name === TOKEN_NOT_AUTHORIZED_ERROR) {
        // dispatch action to show notification here
        // so the error can be seen on the screen
        dispatch(
            showAuthNotification({
                message: TOKEN_NOT_AUTHORIZED_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    // Scenario: facebookInvalidField
    if (error.name === INVALID_SOCIAL_LOGIN_FIELD) {
        // dispatch action to show notification here
        // so the error can be seen on the screen
        // it shows the unknown error message
        dispatch(
            showAuthNotification({
                message: UNKNOWN_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    if (error.name === SUSPENDED_ACCOUNT) {
        // dispatch action to show notification here
        // so the error can be seen on the screen
        // it shows the unknown error message
        dispatch(
            showAuthNotification({
                message: LOCKED_ACCOUNT_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );

        // don't throw any error nor return any rejected promise
        // since this part of the flow is not related with any
        // form field
        return Promise.resolve();
    }

    if (error.name === ARGUMENTS_ERROR) {
        dispatch(
            showAuthNotification({
                message: UNKNOWN_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );

        return Promise.resolve();
    }

    if (error.errors && error.errors.password) {
        let {
            authentication: {
                socialData: { sourceProvider },
            },
        } = state;
        if (
            isUndefined(sourceProvider) &&
            !isEmpty(state.authentication.socialAuthenticationData.apple)
        ) {
            sourceProvider = 'apple';
        }
        trackPasswordIncorrect({ sourceProvider, state });

        // throw the error because is related with
        // the password form field
        return Promise.reject(error);
    }

    if (error.errors && error.errors.type === EXTERNAL_UNKNOWN_ERROR) {
        dispatch(
            showAuthNotification({
                message: UNKNOWN_EXTERNAL_PROVIDER_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );

        return Promise.resolve();
    }

    // Unknown error, let the user know and reject the promise
    dispatch(
        showAuthNotification({
            message: UNKNOWN_ERROR_MESSAGE,
            iconType: <AlertChunky />,
        }),
    );

    return Promise.reject(error);
};

export const submitSuccessRedirect = (url) => () => setWindowLocation(url);

export const sendSoftAccountActivationEmail = () => (dispatch, getState) => {
    const state = getState();
    const { authentication: auth } = state;

    return sendSoftAccountActivationEmailApi(auth.email, auth.referrer)
        .then(() =>
            dispatch(
                push(joinPath(BASE_URL, 'status', SOFT_ACCOUNT_STATUS_PATH)),
            ),
        )
        .then(() =>
            trackEventFromState(state, {
                action: GA_CHECK_EMAIL_PROMPT_EVENT,
                category: GA_AUTH_CATEGORY,
                label: GA_SOFT_ACCT_LABEL,
            }),
        );
};

export const moveBack = () => (dispatch, getState) => {
    const state = getState();
    const {
        routing: { locationBeforeTransitions },
    } = state;
    let {
        authentication: {
            socialData: { sourceProvider },
        },
    } = state;
    if (
        isUndefined(sourceProvider) &&
        !isEmpty(state.authentication.socialAuthenticationData.apple)
    ) {
        sourceProvider = 'apple';
    }
    if (
        locationBeforeTransitions &&
        locationBeforeTransitions.pathname ===
            `${BASE_URL}/${SOCIAL_LOG_IN_PATH}`
    ) {
        dispatch(updateFormType(SOCIAL_LOOK_UP_PATH));
    } else {
        dispatch(cleanSocialAuthenticationData());
        dispatch(updateFormType());
    }
    trackUseAnotherAccountClicked({
        sourceProvider,
        state,
    });
};

export const moveBackCheckout = () => (dispatch, getState) => {
    const state = getState();
    const {
        authentication: {
            socialData: { sourceProvider },
        },
    } = state;

    dispatch(updateCheckoutFormType());

    trackEventFromState(state, {
        action: GA_EDIT_EMAIL_EVENT,
        category: GA_AUTH_CHECKOUT_CATEGORY,
        label: sourceProvider,
    });
};

export const sendResetPasswordEmail = ({
    sourceProvider = EVENTBRITE_PROVIDER,
    mustLinkAccount = false,
}) => (dispatch, getState) => {
    const state = getState();

    trackPasswordForgotten({
        sourceProvider,
        state,
    });

    return sendResetPasswordEmailApi(
        getEmailFromState(getState),
        mustLinkAccount,
        sourceProvider,
    )
        .then(() =>
            dispatch(
                push(joinPath(BASE_URL, 'status', RESET_PASSWORD_STATUS_PATH)),
            ),
        )
        .then(() =>
            trackPasswordForgottenMailSent({
                sourceProvider,
                state,
            }),
        )
        .catch((error) => {
            trackPasswordForgottenMailFailed({
                errorMessage: get(
                    error,
                    'response.parsedError.error',
                    get(error, 'response.statusText', 'Error not defined'),
                ),
                state,
            });
        });
};

export const resetPassword = ({ email }) => (dispatch) => {
    dispatch(setEmail(email));

    return lookupUserApi(email).then((response) =>
        handleResetPasswordAccountStatusResponse(dispatch, response),
    );
};

// Lookup
export const lookupUser = ({ email }) => (dispatch, getState) => {
    if (email) {
        return lookupUserApi(email).then((response) =>
            handleLookupUserResponse(response, dispatch, getState, email),
        );
    }
};

export const lookupUserForCheckout = ({ email }) => (dispatch, getState) =>
    lookupUserApi(email).then((response) =>
        handleLookupUserResponseForCheckout(
            response,
            dispatch,
            getState,
            email,
        ),
    );

export const lookupUserForLinking = ({ email }) => (dispatch, getState) =>
    lookupUserApi(email).then((response) =>
        handleLookupUserResponseForLinking(response, dispatch, getState, email),
    );

export const logInUser = (data) => (dispatch, getState) => {
    const state = getState();
    dispatch(hideCaptcha());
    dispatch(change(LOG_IN_FORM, CAPTCHA_FIELD, null));
    trackLoginClicked({ sourceProvider: EVENTBRITE_PROVIDER, state });

    return logInUserApi(parseFormPostData(getLogInFormData(data, getState)))
        .then((response) => {
            trackLoginSuccessful({
                sourceProvider: EVENTBRITE_PROVIDER,
                state,
            });
            return dispatch(submitSuccessRedirect(response['redirect_url']));
        })
        .catch(handleOnError(dispatch, state));
};

export const logInCheckoutUser = (data) => (dispatch, getState) => {
    const state = getState();

    dispatch(hideCaptcha());
    dispatch(change(LOG_IN_FORM, CAPTCHA_FIELD, null));

    return logInUserApi(parseFormPostData(getLogInFormData(data, getState)))
        .then(() =>
            trackEventFromStateOnPageUnload(state, {
                action: GA_LOGIN_EVENT,
                category: GA_AUTH_CHECKOUT_CATEGORY,
            }),
        )
        .then(() => {
            notifyParent('checkoutLoginSuccessful');
        })
        .catch(handleOnError(dispatch, state, null, GA_AUTH_CHECKOUT_CATEGORY));
};

export const signUpUser = (data) => (dispatch, getState) => {
    const state = getState();

    return signUpUserApi(parseFormPostData(getSignUpFormData(data, getState)))
        .then((response) => {
            let redirectUrl = response['redirect_url'];

            if (shouldOnboardingRedirect(state)) {
                redirectUrl = `/u/interests/?onboarding_referrer=${redirectUrl}`;
            }
            return dispatch(submitSuccessRedirect(redirectUrl));
        })
        .then(() =>
            trackEventFromStateOnPageUnload(state, {
                action: GA_SIGNUP_EVENT,
                category: GA_AUTH_CATEGORY,
            }),
        )
        .catch(handleOnError(dispatch, state));
};

/**
 * Called when:
 *  you are linkin your account at the same time you are logging in
 */
export const linkAccountsOnLogin = (data) => (dispatch, getState) => {
    const state = getState();
    let {
        authentication: {
            socialData: { accessToken, sourceProvider },
            socialAuthenticationData: {
                apple: { accessToken: appleAccessToken } = {},
            },
        },
    } = state;

    /**
     * If we are in the apple flow we re assign this variables.
     * Later we should always use either socialData or socialAuthenticationData, so we dont
     * need to do stuff like this */
    if (appleAccessToken) {
        sourceProvider = SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE;
        accessToken = appleAccessToken;
    }
    dispatch(hideCaptcha());
    dispatch(change(LINK_ACCOUNTS_LOG_IN_FORM, CAPTCHA_FIELD, null));

    return logInUserApi(parseFormPostData(getLogInFormData(data, getState)))
        .then((response) =>
            postAddExternalIdentity('me', sourceProvider, accessToken)
                .then(() => {
                    if (global.window && window.localStorage) {
                        localStorage.removeItem('referrer');
                    }
                    dispatch(
                        showLinkedAccountsDialog(response['redirect_url']),
                    );

                    trackEventFromStateOnPageUnload(state, {
                        action: GA_LINK_ACCOUNTS_LOGIN_EVENT,
                        category: GA_AUTH_CATEGORY,
                        label: sourceProvider,
                    });
                    trackLoginSuccessful({ sourceProvider, state });

                    if (
                        appleAccessToken &&
                        response &&
                        response['redirect_url']
                    ) {
                        dispatch(
                            submitSuccessRedirect(response['redirect_url']),
                        );
                    }
                })
                .catch((error) => {
                    dispatch(submitSuccessRedirect(response['redirect_url']));
                    trackEventFromStateOnPageUnload(getState(), {
                        action: GA_FB_LOGIN_ACCOUNT_LINKING_FAILED,
                        category: GA_AUTH_CATEGORY,
                        label: get(
                            error,
                            'extraData.parsedError.error',
                            get(
                                error,
                                'extraData.response.statusText',
                                'Error not defined',
                            ),
                        ),
                    });
                }),
        )
        .catch(handleOnError(dispatch, state));
};

export const linkAccountOnNewUser = (data) => (dispatch, getState) => {
    const state = getState();
    const {
        authentication: {
            featureFlags: { launchAuthenticationRefactor },
        },
    } = state;

    if (launchAuthenticationRefactor) {
        return handleAppleSocialLogin({
            data: {
                accessToken: data.externalToken,
                sourceProvider: data.externalProvider,
                email: data.email,
            },
            getState,
            dispatch,
            // For now only apple, we should store the social provider separately
            socialProvider: 'apple',
        });
    } else {
        return loginWithTokenApi(data)
            .then((response) => {
                let redirectUrl = '/';

                if (global.window && window.localStorage) {
                    redirectUrl = localStorage.getItem('referrer');
                    localStorage.removeItem('referrer');
                }

                if (shouldOnboardingRedirect(state)) {
                    redirectUrl = `/u/interests/?onboarding_referrer=${redirectUrl}`;
                }
                dispatch(trackSIWA_AccountCreatedWithHiddenEmail());

                return dispatch(submitSuccessRedirect(redirectUrl));
            })
            .catch((response) => {
                trackSIWA_errorOnNewUser(state, response.description);

                handleOnError(
                    dispatch,
                    state,
                )({ name: response.error, message: response.description });
            });
    }
};

export const setPassword = (data) => (dispatch, getState) => {
    const state = getState();
    const currentRoute = state.routing.locationBeforeTransitions.pathname;
    const setPasswordEvent = getSetPasswordEvent(currentRoute);
    const { external_provider } = data;
    const sourceProvider =
        external_provider === 'None' ? EVENTBRITE_PROVIDER : external_provider;

    return setPasswordApi(data)
        .then((response) =>
            dispatch(submitSuccessRedirect(response['redirect_url'])),
        )
        .then(() => {
            trackPasswordReseted({ sourceProvider, state, setPasswordEvent });
            trackLoginSuccessful({ sourceProvider, state });
        })
        .catch((error) => {
            trackPasswordResetedFailed({
                errorMessage: get(
                    error,
                    'errors._error',
                    get(error, 'extraData.response.error'),
                ),
                state,
            });
            //We continue with error to display it to user
            throw error;
        });
};

export const setLinkedAccount = (query) => (dispatch, getState) => {
    const internalToken = query['h'];
    const sourceProvider = query['external_provider'];
    const state = getState();

    dispatch(setIsLoading(true));

    return setLinkedAccountApi(internalToken)
        .then((response) => {
            let apiResult = SET_LINK_ACCOUNT_OK;
            const { error_link_account } = response;
            if (error_link_account == true) {
                // Error on link account but user is logged
                apiResult = SET_LINK_ACCOUNT_OTHER_ERROR;
            }
            dispatch(
                setSocialLinkedAccount({
                    apiResult,
                }),
            );
            const linked = !error_link_account;
            trackAccountLinked({ sourceProvider, state, linked });
            dispatch(setIsLoading(false));
        })
        .catch((error) => {
            let apiResult = SET_LINK_ACCOUNT_ERROR;
            const errorDescription = get(
                error,
                'response.parsedError.error',
                get(error, 'response.statusText', 'Error not defined'),
            );
            const { status } = error.response;

            if (status === 403) {
                // Token Expired
                apiResult = SET_LINK_ACCOUNT_TOKEN_EXPIRED;
            }
            dispatch(
                setSocialLinkedAccount({
                    apiResult,
                }),
            );
            trackAccountLinkFailed({ errorDescription, state });
            dispatch(setIsLoading(false));
        });
};

export const closeButtonClicked = (label) => (dispatch, getState) => {
    const state = getState();
    trackAccountLinkConfirmationClosed({ state, label });
};

export const EXISTING_ACCOUNT_CHECK = 'existingAccountCheckAction';
export const existingAccountCheck = (data) => (dispatch) => {
    dispatch({
        type: 'existingAccountCheckAction',
        payload: data,
    });
};

export const EXISTING_SOFT_ACCOUNT_CHECK = 'existingSoftAccountCheckAction';
export const existingSoftAccountCheck = (data) => (dispatch) => {
    dispatch({
        type: EXISTING_SOFT_ACCOUNT_CHECK,
        payload: data,
    });
};

export const trackSocialProviderClick = ({
    socialProvider,
    stateFromProvider,
}) => (dispatch, getState) => {
    const state = getState();

    trackLoginClicked({
        sourceProvider: socialProvider,
        stateFromProvider,
        state,
    });
};

export const trackEmailConfirmationInput = () => (dispatch, getState) => {
    trackEventFromState(getState(), {
        action: GA_CONFIRM_EMAIL_SHOWED,
        category: GA_AUTH_CATEGORY,
        label: GA_RE_ENTER_EMAIL_LABEL,
    });
};

export const trackEmailMismatchAction = () => (dispatch, getState) => {
    trackEventFromState(getState(), {
        action: GA_EDIT_EMAIL_MISMATCH,
        category: GA_AUTH_CATEGORY,
    });
};

export const trackGetStartedClick = () => (dispatch, getState) => {
    trackLoginClicked({ state: getState() });
};

export const trackLoginInsteadClick = () => (dispatch, getState) => {
    trackEventFromState(getState(), {
        action: GA_LOGIN_INSTEAD_CLICKED_EVENT,
        category: GA_AUTH_CATEGORY,
    });
    dispatch(updateFormType());
};

export const onCheckoutSignInHeightChange = (componentHeight) => (dispatch) => {
    notifyParent('checkoutLoginHeightChange', {
        checkoutLoginHeight: componentHeight,
    });
};
/**
 * Function to handle when a user email only has a FB soft account, but they are not
 * logged into facebook in the browser
 */
export const fbAccountNotConnected = () => (dispatch, getState) => {
    trackEventFromState(getState(), {
        action: GA_FB_LOGIN_NOT_LOGGED_IN_TO_FB,
        category: GA_AUTH_CHECKOUT_CATEGORY,
    });

    dispatch(
        showAuthNotification({
            message: FACEBOOK_ACCOUNT_NOT_LOGGED_IN_ERROR,
            iconType: <AlertChunky />,
        }),
    );
    notifyParent('checkoutLoginSoftAccount');
};

/* Function to send email to link account */
export const sendEmailLinkAccount = () => (dispatch, getState) => {
    const state = getState();
    const {
        authentication: {
            socialData: { sourceProvider },
        },
    } = state;

    dispatch(setShowModal(true));
    dispatch(setIsLoading(true));

    trackMagicLinkClicked({ sourceProvider, state });

    return sendEmailLinkAccountApi(getEmailFromState(getState), sourceProvider)
        .then((response) => {
            trackMagicLinkSent({
                sourceProvider: sourceProvider,
                state,
            });
            dispatch(setSendEmailLinkAccount(true));
            dispatch(setIsLoading(false));

            /**
             * In dev we benefit from having this mocked as it allow us to test the flow
             * without needing an email service.
             * Enables local testing and cypress testing.
             */
            if (
                process.env.NODE_ENV === 'development' &&
                response &&
                response.skipEmailDelivery
            ) {
                setTimeout(
                    dispatch(
                        push(
                            joinPath(
                                BASE_URL,
                                `${LINK_SOCIAL_ACCOUNT_PATH}/?h=123456ABCDEF&external_provider=facebook`,
                            ),
                        ),
                    ),
                    5000,
                );
            }
        })
        .catch((error) => {
            trackMagicLinkFailed({
                errorMessage: get(
                    error,
                    'response.parsedError.error',
                    get(error, 'response.statusText', 'Error not defined'),
                ),
                state,
            });
            dispatch(setSendEmailLinkAccount(false));
            dispatch(setIsLoading(false));
        });
};

export const redirectToSignUp = () => (dispatch, getState) => {
    const state = getState();

    trackSignUpClicked(state);
    dispatch(push(joinPath(BASE_URL, '/signin/signup')));
};

export const viewPassword = () => (dispatch, getState) => {
    const state = getState();
    const {
        authentication: {
            socialData: { sourceProvider },
        },
    } = state;
    trackPasswordViewed({ sourceProvider, state });
};
export const closeMagicLinkModal = () => (dispatch, getState) => {
    const state = getState();
    trackMagicLinkConfirmationClosed({ state });
};
